/**
 * useRealtimeInvalidation (admin)
 *
 * Subscribes to the signed-in platform admin's private Supabase broadcast channel
 * (`admin:{adminId}`) and invalidates the admin-notification + support caches when
 * the server signals a change. No PII on the channel; data is refetched via the
 * authenticated API.
 */
import { useEffect } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAdmin } from "@/stores/authStore";

/** Subscribe-and-invalidate for the current admin. No-op when signed out. */
export function useRealtimeInvalidation(): void {
  const queryClient = useQueryClient();
  const adminId = useAdmin()?.id;

  useEffect(() => {
    if (!adminId) return;
    let channel: RealtimeChannel | null = null;
    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      if (!token || cancelled) return;
      void supabase.realtime.setAuth(token);
      channel = supabase
        .channel(`admin:${adminId}`, { config: { private: true } })
        .on("broadcast", { event: "change" }, () => {
          // The admin bell + Support inbox + any open thread.
          void queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
          void queryClient.invalidateQueries({ queryKey: ["support-conversations"] });
          void queryClient.invalidateQueries({ queryKey: ["support-messages"] });
        })
        .subscribe();
    });

    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [adminId, queryClient]);
}
